import { Popover,Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { FC,Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonThird from "shared/Button/ButtonThird";
import Checkbox from "shared/Checkbox/Checkbox";
import { useGetCategoriesWithProductsQuery } from "store/api/categories";
import { useCollege } from "store/utils/hooks";
import Sort from "./Sort";

export interface HeaderFilterSearchPageProps {
  className?: string;
}

export const Categories = () => {
  const [params, setParams] = useSearchParams()
  const college = useCollege()
  const { data: categories, isLoading, error } = useGetCategoriesWithProductsQuery({ collegeId: college.id })
  const selectedCategory = params.get('category') ? Number(params.get('category')) : null

  const handleCategoryChange = (categoryId: number, checked: boolean) => {
    const newParams = { ...Object.fromEntries(params) }
    if (checked) {
      newParams.category = categoryId.toString()
      // Remove categories if it exists
      delete newParams.categories
    } else {
      delete newParams.category
    }
    setParams(newParams)
  }

  const handleAllCategories = (checked: boolean) => {
    const newParams = { ...Object.fromEntries(params) }
    if (checked) {
      delete newParams.category
      delete newParams.categories
    }
    setParams(newParams)
  }

  if (isLoading) return <div>Loading categories...</div>
  if (error) return <div>Error loading categories</div>

  return <>
    <div className="flex items-center space-x-2">
      <Checkbox
        name="all-categories"
        label="Todas las categorías"
        checked={!selectedCategory}
        onChange={(checked) => handleAllCategories(checked)}
      />
    </div>
    {categories?.map(
      (item) => (
        <div key={item.id} className="flex items-center space-x-2">
          <Checkbox
            name={`category-${item.id}`}
            label={item.name}
            checked={selectedCategory === item.id}
            onChange={(checked) => handleCategoryChange(item.id, checked)}
          />
        </div>
      )
    )}
  </>
}

const CategoriesFilter = () => {
  const [params, setParams] = useSearchParams()
  const college = useCollege()
  const { data: categories } = useGetCategoriesWithProductsQuery({ collegeId: college.id })
  const selectedCategory = params.get('category') ? Number(params.get('category')) : null

  const handleCategoryChange = (categoryId: number, checked: boolean) => {
    const newParams = { ...Object.fromEntries(params) }
    if (checked) {
      newParams.category = categoryId.toString()
      delete newParams.categories
    } else {
      delete newParams.category
    }
    setParams(newParams)
  }

  const handleAllCategories = (checked: boolean) => {
    const newParams = { ...Object.fromEntries(params) }
    if (checked) {
      delete newParams.category
      delete newParams.categories
    }
    setParams(newParams)
  }

  const renderXClear = () => {
    return (
      <span className="flex-shrink-0 w-4 h-4 rounded-full bg-primary-500 text-white flex items-center justify-center ml-3 cursor-pointer">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-3 w-3"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </span>
    );
  };

  const renderTabsCategoriesFilter = () => {
    return (
      <Popover className="relative">
        {({ open, close }) => (
          <>
            <Popover.Button
              className={`flex items-center justify-center px-4 py-2 text-sm border rounded-full focus:outline-none select-none
                ${open ? "!border-primary-500 " : ""}
                  ${!!selectedCategory
                    ? "!border-primary-500 bg-primary-50 text-primary-900"
                    : "border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:border-neutral-400 dark:hover:border-neutral-500"
                  }
                  `}
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 20 20" fill="none">
                <path
                  d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 01-1.414 0L3.293 7.293A1 1 0 013 6.586V4z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              <span className="mr-2">
                {selectedCategory
                  ? categories?.find(cat => cat.id === selectedCategory)?.name || "Categoría seleccionada"
                  : "Filtrar por categorías"
                }
              </span>
              {!selectedCategory ? (
                <ChevronDownIcon className="w-4 h-4" />
              ) : (
                <span onClick={() => handleAllCategories(true)}>
                  {renderXClear()}
                </span>
              )}
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute z-40 w-screen max-w-sm px-4 mt-3 right-0 sm:px-0 lg:max-w-sm">
                <div className="overflow-hidden rounded-2xl shadow-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700">
                  <div className="relative flex flex-col px-5 py-6 space-y-5">
                    <div className="flex items-center">
                      <Checkbox
                        name="all-categories"
                        label="Todas las categorías"
                        checked={!selectedCategory}
                        onChange={(checked) => handleAllCategories(checked)}
                      />
                    </div>
                    {categories?.map((item) => (
                      <div key={item.id} className="flex items-center">
                        <Checkbox
                          name={`category-${item.id}`}
                          label={item.name}
                          checked={selectedCategory === item.id}
                          onChange={(checked) => handleCategoryChange(item.id, checked)}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="p-5 bg-neutral-50 dark:bg-neutral-900 dark:border-t dark:border-neutral-800 flex items-center justify-between">
                    <ButtonThird
                      onClick={() => {
                        close();
                        handleAllCategories(true);
                      }}
                      sizeClass="px-4 py-2 sm:px-5"
                    >
                      Limpiar
                    </ButtonThird>
                    <ButtonPrimary
                      onClick={close}
                      sizeClass="px-4 py-2 sm:px-5"
                    >
                      Cerrar
                    </ButtonPrimary>
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    );
  };

  return (
    <div className="flex lg:space-x-4">
      <div className="flex flex-1 space-x-4">
        <div>{renderTabsCategoriesFilter()}</div>
      </div>
    </div>
  );
};

const HeaderFilterSearchPage: FC<HeaderFilterSearchPageProps> = ({
  className = "mb-12",
}) => {

  return (
    <div className={`flex flex-col relative ${className}`}>
      <div className="flex flex-col lg:flex-row lg:items-center justify-end space-y-6 lg:space-y-0 lg:space-x-2">
        {/* Categories filter dropdown */}
        <div className="flex justify-start">
          <CategoriesFilter />
        </div>

        {/* Sort dropdown */}
        <div className="flex justify-end">
          <Sort/>
        </div>
      </div>
    </div>
  );
};

export default HeaderFilterSearchPage;
